import { PLAN_CONFIG } from './plans';

/** @typedef {'en' | 'ja' | 'fr' | 'es'} CheckoutV2Locale */

const JA_PLAN_COPY = {
  vpn_1_day: { label: '1日間アクセス', description: '1日間トライアル' },
  vpn_30_day: { label: '30日間アクセス', description: '1ヶ月メンバーシップ' },
  vpn_90_day: { label: '90日間アクセス', description: '3ヶ月メンバーシップ' },
  vpn_365_day: { label: '365日間アクセス', description: '1年メンバーシップ' },
};

const FR_PLAN_COPY = {
  vpn_1_day: { label: "Accès 1 jour", description: 'Essai 1 jour' },
  vpn_30_day: { label: 'Accès 30 jours', description: 'Abonnement 1 mois' },
  vpn_90_day: { label: 'Accès 90 jours', description: 'Abonnement 3 mois' },
  vpn_365_day: { label: 'Accès 365 jours', description: 'Abonnement 1 an' },
};

const ES_PLAN_COPY = {
  vpn_1_day: { label: 'Acceso 1 día', description: 'Prueba 1 día' },
  vpn_30_day: { label: 'Acceso 30 días', description: 'Membresía 1 mes' },
  vpn_90_day: { label: 'Acceso 90 días', description: 'Membresía 3 meses' },
  vpn_365_day: { label: 'Acceso 365 días', description: 'Membresía 1 año' },
};

/**
 * @param {URLSearchParams | null | undefined} searchParams
 * @returns {CheckoutV2Locale}
 */
export function getCheckoutV2Locale(searchParams) {
  const raw = searchParams?.get?.('locale');
  if (raw == null || raw === '') return 'en';
  const lower = String(raw).toLowerCase();
  if (lower === 'ja') return 'ja';
  if (lower === 'fr') return 'fr';
  if (lower === 'es') return 'es';
  return 'en';
}

/**
 * @param {string} selectedPlanKey
 * @param {CheckoutV2Locale} locale
 */
export function getLocalizedPlanAmount(selectedPlanKey, locale) {
  const base = PLAN_CONFIG[selectedPlanKey] || PLAN_CONFIG.vpn_30_day;
  if (locale === 'ja') {
    const ja = JA_PLAN_COPY[selectedPlanKey] || JA_PLAN_COPY.vpn_30_day;
    return { ...base, label: ja.label, description: ja.description };
  }
  if (locale === 'fr') {
    const fr = FR_PLAN_COPY[selectedPlanKey] || FR_PLAN_COPY.vpn_30_day;
    return { ...base, label: fr.label, description: fr.description };
  }
  if (locale === 'es') {
    const es = ES_PLAN_COPY[selectedPlanKey] || ES_PLAN_COPY.vpn_30_day;
    return { ...base, label: es.label, description: es.description };
  }
  return base;
}

const EN_STRINGS = {
  documentTitle: 'Checkout',
  completeRegistration: 'Complete Your Registration',
  useVpnUnlock: 'Use VPN to unlock:',
  streamingServices: 'Streaming services',
  onlineContent: 'Online Content',
  socialMedia: 'Social Media',
  emailLabel: 'Email address',
  emailPlaceholder: 'you@example.com',
  orderSummary: 'Order summary',
  plan: 'Plan',
  billing: 'Billing',
  billingOnce: 'Once',
  totalDueToday: 'Total due today',
  accountLabel: 'Account:',
  payNow: 'Signup Now',
  processing: 'Processing...',
  firstName: 'First name',
  lastName: 'Last name',
  cardNumber: 'Card number',
  expiry: 'Expiry',
  cvv: 'CVV',
  country: 'Country',
  state: 'State',
  province: 'Province',
  stateOrProvince: 'State / province',
  zip: 'ZIP',
  postalCode: 'Postal code',
  selectCountry: 'Select country',
  selectRegion: 'Select',
  fillPaymentFields: 'Please fill in all payment fields.',
  invalidCard: 'Enter a valid card number.',
  invalidExpiry: 'Enter a valid expiry (MM/YY).',
  invalidCvv: 'Enter a valid CVV.',
  loadingPaymentForm: 'Loading payment form...',
  failedLoadPaymentForm: 'Failed to load payment form',
  paymentFailed: 'Payment failed',
  errorOccurred: 'An error occurred',
  pleaseEnterEmail: 'Please enter your email address',
  emptyServerResponse:
    'Empty response from server. Check function logs.',
  invalidServerResponse: 'Invalid response from server',
  activeSubscriptionFallback:
    'This email already has an active plan.',
  invalidMissingSecret: 'Invalid response from server (missing clientSecret)',
  initCheckoutFailed:
    'Failed to initialize checkout. Please try again.',
  missingSession:
    'Missing session. Please go to the success page from your payment confirmation email or try again.',
  loading: 'Loading...',
  continueSecurePayment: 'Continue to secure payment',
  logInInstead: 'Log in instead →',
  agreePrefix: 'By continuing you agree to our ',
  termsOfService: 'Terms of Service',
  andWord: 'and',
  privacyPolicy: 'Privacy Policy',
  agreeSuffix: '.',
  secure: 'Secure',
  encrypted: 'Encrypted',
  pciDss: 'PCI DSS',
  vpnLogoAlt: 'VPN logo',
  productNamePrefix: 'VPN Subscription - ',
};

const JA_STRINGS = {
  documentTitle: 'チェックアウト',
  completeRegistration: '登録を完了する',
  useVpnUnlock: 'VPNで利用できるもの：',
  streamingServices: 'ストリーミングサービス',
  onlineContent: 'オンラインコンテンツ',
  socialMedia: 'ソーシャルメディア',
  emailLabel: 'メールアドレス',
  emailPlaceholder: 'you@example.com',
  orderSummary: 'ご注文内容',
  plan: 'プラン',
  billing: '請求',
  billingOnce: '一回限り',
  totalDueToday: '本日お支払い合計',
  accountLabel: 'アカウント：',
  payNow: '今すぐ登録',
  processing: '処理中…',
  firstName: '名',
  lastName: '姓',
  cardNumber: 'カード番号',
  expiry: '有効期限',
  cvv: 'CVV',
  country: '国',
  state: '州',
  province: '州・省',
  stateOrProvince: '州 / 都道府県',
  zip: 'ZIP',
  postalCode: '郵便番号',
  selectCountry: '国を選択',
  selectRegion: '選択',
  fillPaymentFields: 'お支払い情報をすべて入力してください。',
  invalidCard: '有効なカード番号を入力してください。',
  invalidExpiry: '有効期限を MM/YY で入力してください。',
  invalidCvv: '有効な CVV を入力してください。',
  loadingPaymentForm: 'お支払いフォームを読み込み中…',
  failedLoadPaymentForm: 'お支払いフォームの読み込みに失敗しました',
  paymentFailed: 'お支払いに失敗しました',
  errorOccurred: 'エラーが発生しました',
  pleaseEnterEmail: 'メールアドレスを入力してください',
  emptyServerResponse:
    'サーバーから空の応答がありました。関数のログを確認してください。',
  invalidServerResponse: 'サーバーからの応答が無効です',
  activeSubscriptionFallback:
    'このメールアドレスには既に有効なサブスクリプションがあります。',
  invalidMissingSecret:
    'サーバーからの応答が無効です（clientSecret がありません）',
  initCheckoutFailed:
    'チェックアウトの初期化に失敗しました。もう一度お試しください。',
  missingSession:
    'セッションが見つかりません。お支払い確認メールから完了ページへ進むか、もう一度お試しください。',
  loading: '読み込み中…',
  continueSecurePayment: '安全なお支払いへ進む',
  logInInstead: 'ログインする →',
  agreePrefix: '続行すると、当社の',
  termsOfService: '利用規約',
  andWord: 'および',
  privacyPolicy: 'プライバシーポリシー',
  agreeSuffix: 'に同意したことになります。',
  secure: '安全',
  encrypted: '暗号化',
  pciDss: 'PCI DSS',
  vpnLogoAlt: 'VPNロゴ',
  productNamePrefix: 'VPNサブスクリプション - ',
};

const FR_STRINGS = {
  documentTitle: 'Paiement',
  completeRegistration: 'Finalisez votre inscription',
  useVpnUnlock: 'Avec le VPN, débloquez :',
  streamingServices: 'Des services de streaming',
  onlineContent: 'Du contenu en ligne',
  socialMedia: 'Des réseaux sociaux',
  emailLabel: 'Adresse e-mail',
  emailPlaceholder: 'vous@exemple.com',
  orderSummary: 'Résumé de la commande',
  plan: 'Formule',
  billing: 'Facturation',
  billingOnce: 'Unique',
  totalDueToday: "Total à payer aujourd'hui",
  accountLabel: 'Compte :',
  payNow: "S'inscrire maintenant",
  processing: 'Traitement…',
  firstName: 'Prénom',
  lastName: 'Nom',
  cardNumber: 'Numéro de carte',
  expiry: 'Expiration',
  cvv: 'CVV',
  country: 'Pays',
  state: 'État',
  province: 'Province',
  stateOrProvince: 'État / province',
  zip: 'ZIP',
  postalCode: 'Code postal',
  selectCountry: 'Choisir un pays',
  selectRegion: 'Choisir',
  fillPaymentFields: 'Veuillez renseigner tous les champs de paiement.',
  invalidCard: 'Saisissez un numéro de carte valide.',
  invalidExpiry: 'Saisissez une date d’expiration valide (MM/AA).',
  invalidCvv: 'Saisissez un CVV valide.',
  loadingPaymentForm: 'Chargement du formulaire de paiement…',
  failedLoadPaymentForm: 'Impossible de charger le formulaire de paiement',
  paymentFailed: 'Le paiement a échoué',
  errorOccurred: "Une erreur s'est produite",
  pleaseEnterEmail: 'Veuillez saisir votre adresse e-mail',
  emptyServerResponse:
    'Réponse vide du serveur. Vérifiez les journaux de la fonction.',
  invalidServerResponse: 'Réponse du serveur invalide',
  activeSubscriptionFallback:
    'Cette adresse e-mail a déjà un abonnement actif.',
  invalidMissingSecret:
    'Réponse du serveur invalide (clientSecret manquant)',
  initCheckoutFailed:
    "Impossible d'initialiser le paiement. Veuillez réessayer.",
  missingSession:
    "Session introuvable. Ouvrez la page de confirmation depuis l'e-mail de paiement ou réessayez.",
  loading: 'Chargement…',
  continueSecurePayment: 'Continuer vers le paiement sécurisé',
  logInInstead: 'Se connecter →',
  agreePrefix: 'En continuant, vous acceptez nos ',
  termsOfService: "Conditions d'utilisation",
  andWord: 'et',
  privacyPolicy: 'Politique de confidentialité',
  agreeSuffix: '.',
  secure: 'Sécurisé',
  encrypted: 'Chiffré',
  pciDss: 'PCI DSS',
  vpnLogoAlt: 'Logo VPN',
  productNamePrefix: 'Abonnement VPN - ',
};

const ES_STRINGS = {
  documentTitle: 'Pago',
  completeRegistration: 'Completa tu registro',
  useVpnUnlock: 'Con la VPN, desbloquea:',
  streamingServices: 'Servicios de streaming',
  onlineContent: 'Contenido en línea',
  socialMedia: 'Redes sociales',
  emailLabel: 'Correo electrónico',
  emailPlaceholder: 'tu@ejemplo.com',
  orderSummary: 'Resumen del pedido',
  plan: 'Plan',
  billing: 'Facturación',
  billingOnce: 'Una vez',
  totalDueToday: 'Total a pagar hoy',
  accountLabel: 'Cuenta:',
  payNow: 'Registrarse ahora',
  processing: 'Procesando…',
  firstName: 'Nombre',
  lastName: 'Apellido',
  cardNumber: 'Número de tarjeta',
  expiry: 'Caducidad',
  cvv: 'CVV',
  country: 'País',
  state: 'Estado',
  province: 'Provincia',
  stateOrProvince: 'Estado / provincia',
  zip: 'ZIP',
  postalCode: 'Código postal',
  selectCountry: 'Selecciona un país',
  selectRegion: 'Selecciona',
  fillPaymentFields: 'Completa todos los campos de pago.',
  invalidCard: 'Introduce un número de tarjeta válido.',
  invalidExpiry: 'Introduce una caducidad válida (MM/AA).',
  invalidCvv: 'Introduce un CVV válido.',
  loadingPaymentForm: 'Cargando formulario de pago…',
  failedLoadPaymentForm: 'No se pudo cargar el formulario de pago',
  paymentFailed: 'El pago ha fallado',
  errorOccurred: 'Ha ocurrido un error',
  pleaseEnterEmail: 'Introduce tu correo electrónico',
  emptyServerResponse:
    'Respuesta vacía del servidor. Consulta los registros de la función.',
  invalidServerResponse: 'Respuesta no válida del servidor',
  activeSubscriptionFallback:
    'Este correo ya tiene una suscripción activa.',
  invalidMissingSecret:
    'Respuesta no válida del servidor (falta clientSecret)',
  initCheckoutFailed:
    'No se pudo iniciar el pago. Inténtalo de nuevo.',
  missingSession:
    'Falta la sesión. Abre la página de confirmación desde el correo de pago o inténtalo de nuevo.',
  loading: 'Cargando…',
  continueSecurePayment: 'Continuar al pago seguro',
  logInInstead: 'Iniciar sesión →',
  agreePrefix: 'Al continuar, aceptas nuestros ',
  termsOfService: 'Términos del servicio',
  andWord: 'y',
  privacyPolicy: 'Política de privacidad',
  agreeSuffix: '.',
  secure: 'Seguro',
  encrypted: 'Cifrado',
  pciDss: 'PCI DSS',
  vpnLogoAlt: 'Logotipo VPN',
  productNamePrefix: 'Suscripción VPN - ',
};

/**
 * @param {CheckoutV2Locale} locale
 */
export function checkoutV2Strings(locale) {
  if (locale === 'ja') return JA_STRINGS;
  if (locale === 'fr') return FR_STRINGS;
  if (locale === 'es') return ES_STRINGS;
  return EN_STRINGS;
}

/** Geo line copy for checkout header (shared with CheckoutGeoipInfo). */
const GEO_UI = {
  en: {
    loading: 'Looking up your connection…',
    ipPrefix: 'IP address: ',
    locationPrefix: 'Location: ',
  },
  ja: {
    loading: '接続情報を確認しています…',
    ipPrefix: 'IPアドレス: ',
    locationPrefix: '所在地: ',
  },
  fr: {
    loading: 'Analyse de votre connexion…',
    ipPrefix: 'Adresse IP : ',
    locationPrefix: 'Localisation : ',
  },
  es: {
    loading: 'Consultando tu conexión…',
    ipPrefix: 'Dirección IP: ',
    locationPrefix: 'Ubicación: ',
  },
};

/**
 * @param {CheckoutV2Locale} locale
 */
export function checkoutGeoipUi(locale) {
  return GEO_UI[locale] || GEO_UI.en;
}
